import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  #ogSauceTitles = [
    'Reaper Hot Sauce',
    'Ghost Pepper Hot Sauce',
    'Mango Habanero Hot Sauce',
    'Garlic Habanero Hot Sauce',
    'Serrano Hot Sauce',
    'Chipotle Hot Sauce',
  ];

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#updateOGHeatwaveCart();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')
        ?.shadowRoot
        ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal =
      /** @type {HTMLDialogElement | null} */ (
        event.target instanceof Element
          ? event.target.closest('dialog:modal')
          : null
      );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => {
            this.#updateStickyState();
            this.#updateOGHeatwaveCart();
          });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  async #updateOGHeatwaveCart() {
    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart.js`, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return;

      const cart = await response.json();

      if (!cart.items?.length) {
        this.querySelector('#og-cart-promo')?.remove();
        return;
      }

      this.#updateOGHeatwavePromo(cart);

      this.#injectOGHeatwaveStyles();
    } catch (error) {
      console.warn(
        '[OG Heatwave] Cart update failed:',
        error
      );
    }
  }

  /*
   * ==========================================
   * PHASE 1
   * SAVINGS + FREE SHIPPING PROGRESS BAR
   * ==========================================
   */
  #updateOGHeatwavePromo(cart) {
    const cartTotal = cart.total_price / 100;
    const itemCount = cart.item_count;

    const freeShippingThreshold = 50;

    const amountRemaining = Math.max(
      freeShippingThreshold - cartTotal,
      0
    );

    const progressPercent = Math.min(
      (cartTotal / freeShippingThreshold) * 100,
      100
    );

    let promo =
      this.querySelector('#og-cart-promo');

    if (!promo) {
      promo = document.createElement('div');

      promo.id = 'og-cart-promo';

      promo.innerHTML = `
        <div class="og-cart-promo-inner">

          <div
            class="og-cart-promo-message"
            data-og-message
          ></div>

          <div
            class="og-cart-promo-submessage"
            data-og-submessage
          ></div>

          <div class="og-cart-progress-track">
            <div
              class="og-cart-progress-bar"
              data-og-progress
            ></div>
          </div>

        </div>
      `;

      const dialog = this.#dialog;

      const summary =
        dialog?.querySelector(
          '.cart-drawer__summary'
        );

      const content =
        dialog?.querySelector(
          '.cart-drawer__content'
        );

      if (summary?.parentNode) {
        summary.parentNode.insertBefore(
          promo,
          summary
        );
      } else if (content) {
        content.appendChild(promo);
      } else {
        this.appendChild(promo);
      }
    }

    const message =
      promo.querySelector(
        '[data-og-message]'
      );

    const submessage =
      promo.querySelector(
        '[data-og-submessage]'
      );

    const progressBar =
      promo.querySelector(
        '[data-og-progress]'
      );

    if (progressBar) {
      progressBar.style.width =
        `${progressPercent}%`;
    }

    /*
     * $50+
     */
    if (cartTotal >= freeShippingThreshold) {
      if (message) {
        message.innerHTML =
          '🚚 <strong>FREE SHIPPING UNLOCKED!</strong>';
      }

      if (submessage) {
        submessage.innerHTML =
          'Your order ships free.';
      }

      promo.classList.add(
        'og-cart-promo--complete'
      );

      return;
    }

    promo.classList.remove(
      'og-cart-promo--complete'
    );

    /*
     * 2+ bottles
     */
    if (itemCount >= 2) {
      if (message) {
        message.innerHTML =
          '🔥 <strong>2+ BOTTLE SAVINGS UNLOCKED</strong>';
      }

      if (submessage) {
        submessage.innerHTML =
          `Only <strong>$${amountRemaining.toFixed(
            2
          )}</strong> away from FREE SHIPPING`;
      }

      return;
    }

    /*
     * 1 bottle
     */
    const bottlesNeeded =
      Math.max(2 - itemCount, 0);

    if (message) {
      message.innerHTML =
        '🔥 <strong>STOCK UP &amp; SAVE</strong>';
    }

    if (submessage) {
      submessage.innerHTML =
        `Add <strong>${bottlesNeeded} more ${
          bottlesNeeded === 1
            ? 'bottle'
            : 'bottles'
        }</strong> to unlock $10 pricing`;
    }
  }

  #escapeHTML(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  /*
   * ==========================================
   * OG HEATWAVE CSS
   * ==========================================
   */
  #injectOGHeatwaveStyles() {
    if (
      document.getElementById(
        'og-cart-promo-styles'
      )
    ) {
      return;
    }

    const style =
      document.createElement('style');

    style.id =
      'og-cart-promo-styles';

    style.textContent = `

      /* ---------------------------
         PHASE 1
         --------------------------- */

      #og-cart-promo {
        width: 100%;
        box-sizing: border-box;

        padding:
          0 24px;

        margin:
          12px 0 10px;
      }

      .og-cart-promo-inner {
        width: 100%;
        box-sizing: border-box;

        border:
          1px solid
          rgba(75, 0, 0, 0.14);

        border-radius: 8px;

        padding:
          10px 14px 11px;

        text-align: center;
      }

      .og-cart-promo-message {
        font-size: 12px;

        line-height: 1.25;

        letter-spacing:
          0.35px;

        margin-bottom: 3px;
      }

      .og-cart-promo-message strong {
        font-weight: 800;
      }

      .og-cart-promo-submessage {
        font-size: 12px;

        line-height: 1.3;

        opacity: 0.78;

        margin-bottom: 8px;
      }

      .og-cart-promo-submessage strong {
        font-weight: 800;
      }

      .og-cart-progress-track {
        width: 100%;

        height: 5px;

        border-radius:
          999px;

        background:
          rgba(
            75,
            0,
            0,
            0.10
          );

        overflow: hidden;
      }

      .og-cart-progress-bar {
        width: 0%;

        height: 100%;

        border-radius:
          inherit;

        background:
          currentColor;

        transition:
          width
          0.35s
          ease;
      }

      .og-cart-promo--complete
      .og-cart-progress-bar {
        width:
          100% !important;
      }


      /* ---------------------------
         MOBILE
         --------------------------- */

      @media screen and
      (max-width: 749px) {

        #og-cart-promo {
          padding:
            0 16px;

          margin:
            10px 0 9px;
        }

        .og-cart-promo-inner {
          padding:
            9px 11px 10px;
        }

        .og-cart-promo-message {
          font-size: 11px;
        }

        .og-cart-promo-submessage {
          font-size: 11px;

          margin-bottom:
            7px;
        }

        .og-cart-progress-track {
          height: 4px;
        }

      }
    `;

    document.head.appendChild(
      style
    );
  }

  /*
   * EXISTING SHOPIFY STICKY LOGIC
   */
  #updateStickyState() {
    const dialog =
      this.#dialog;

    if (!dialog) return;

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog
        .getBoundingClientRect()
        .height;

    const summaryHeight =
      summary
        .getBoundingClientRect()
        .height;

    const ratio =
      summaryHeight /
      drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio >
        this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}